import React, { useState, useCallback, useRef, useEffect, LegacyRef } from "react";
import { useTheme, Headline, Switch, Portal, Modal } from "react-native-paper";
import { useSelector, useDispatch } from "react-redux";
import { View, StyleSheet, Pressable, Platform } from "react-native";
import NetInfo from "@react-native-community/netinfo";
import { Spacer } from "../../components/spacer/spacer";
import { Screen, Text, Button, InfoBox } from "../../components";
import { Stack } from "../../components/layout/stack";
import { PaymentTable } from "../../components/payment-table/payment-table";
import { TransactionsTable } from "../../components/transactions-table/transactions-table";
import { HistoryBottomSheet } from "../../components/bottom-sheet";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { isBalanceError, toHuman, toDollars } from "../../utils/money";
import { translate } from "../../i18n/translate";
import { ThemeType } from "../../theme/theme";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";

import { getPropertyFees, setPaymentCancelled } from "../../redux/actions/actionCreators";
import { DomusoApiService } from "../../api/DomusoApiService";
import { ErrorMessage } from "../../components/error-message/error-message";
import { nanoid } from "nanoid/non-secure";
import { InfoBoxType, AccountSystemTypes } from "../../enums";
import { convertPstDateIntoLocalTime, getDisplayableLocalTime } from "../../utils/date";
import { deriveUnitTitle, getPropertyAddressFromPropertyProfile } from "../../utils";
import { navigateToHomePage } from "../../utils/navigateToHomePage";
import { AllowedPaymentMethods } from "../../enums";
import { RootState } from "../../redux/reducers";
import { testProps } from "../../utils/testProps";
import { upperCase } from "lodash";
import moment from "moment";
import { useReservationInformation } from "../../hooks/useReservationInformation";
import { isDeviceVirtual } from "../../utils/virtualDevice";
import { domusoResponsiveStyleHelper } from "../new/utils/domuso-responsive";
import { Grid, Container, Row, Column } from "ui-grid";
import { OnScreenAlert, fontFamily } from "ui-components";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";
import automationTestingLocators from "../../screens/new/utils/automation-testing-locators.json";

const constants = require("domuso-constants");
const { PaymentTypes } = constants.payment;

enum TableTypes {
  PAYMENTS = "payments",
  TRANSACTIONS = "transactions",
}

export const SpecificReservation = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const theme = useTheme() as ThemeType;
  const dispatch = useDispatch();
  const styles = getStyles(theme);
  const sheetRef = useRef<any>(null);
  const { params } = route;
  const logger = useSelector((state: RootState) => state.logger);
  const {
    accountingId,
    yardiId,
    accountingDetails,
    accountSystemType,
    activeAutoPay,
    allowedPayments,
    balanceDue,
    currentProperty,
    currentPropertyFeeProfile,
    currentPropertyProfile,
    isBlacklisted,
    propName,
    propertyUnit,
    reservationId,
    hasEnabledPaymentMethods,
    isAimPastResident,
    paymentHistorySelected = true,
  } = params;
  logger.info(`Viewing reservation@${reservationId}`);
  let {
    is_integrated: isIntegrated,
    is_blocked: isBlocked,
    household_id: householdId,
    residentStatus,
  } = accountingDetails || {};
  let [paymentDisabledMessage, allowAutoPay] = getPaymentValidationMessage({
    allowedPayments,
    isBlacklisted,
    isBlocked,
    isIntegrated,
    hasEnabledPaymentMethods: !!hasEnabledPaymentMethods,
    balanceDue,
  });

  const transactionsFromPMS =
    accountSystemType === AccountSystemTypes.YARDI ||
    accountSystemType === AccountSystemTypes.AIM ||
    accountSystemType === AccountSystemTypes.RESMAN ||
    accountSystemType === AccountSystemTypes.REALPAGE;
  const canViewAccountHistory =
    currentPropertyProfile?.allow_account_history && transactionsFromPMS;
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedTableType, setSelectedTableType] = useState<TableTypes>(TableTypes.PAYMENTS);
  const [sheetData, setSheetData] = useState(null);
  const [sheetDataIndex, setSheetDataIndex] = useState<number>(0);
  const [isPaymentsSelected, setIsPaymentsSelected] = useState(paymentHistorySelected);
  const [paymentsError, setPaymentsError] = useState<any>(null);
  const [transactionsError, setTransactionsError] = useState<any>(null);
  const [networkError, setNetworkError] = useState<any>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showAllowPaymentsError, setShowAllowPaymentsError] = useState<boolean>(false);
  const [scheduledPayments, setScheduledPayments] = useState([]);
  const [visibleModal, setVisibleModal] = useState(false);
  const [isAutopayCancelLoading, setAutopayCancelLoading] = useState<any>(false);
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);
  const { FUTURE, APPLICANT } = constants.reservation.ResidentStatus;
  const isNewResident = [FUTURE, APPLICANT].includes(residentStatus);
  const [isSwitchOn, setIsSwitchOn] = useState(
    allowedPayments === "ANY" && !!activeAutoPay?.schedule_date
  );
  const [apiError, setApiError] = useState<boolean>(false);
  const tableRowMaxIndex =
    selectedTableType === TableTypes.PAYMENTS ? payments.length - 1 : transactions.length - 1;
  const sheetDataValues =
    selectedTableType === TableTypes.PAYMENTS
      ? payments[sheetDataIndex]
      : transactions[sheetDataIndex];
  const showLeftNavButton = sheetDataIndex !== 0;
  const showRightNavButton = sheetDataIndex !== tableRowMaxIndex;
  const { appMode } = useReservationInformation();
  const [netInfo, setNetInfo] = useState<boolean>(true);

  const propAddress = getPropertyAddressFromPropertyProfile(currentPropertyProfile);

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentCancelled());
    setPaymentsError(null);
    if (isDeviceVirtual() || netInfo) {
      setLoading(true);
      domusoApiService
        .getPaymentHistory(reservationId)
        .then((resp) => {
          const [scheduledPayments, regularPayments] = resp.reduce(
            (paymentsTuple: any[], currentPayment: any) => {
              if (currentPayment.status === "Scheduled") {
                paymentsTuple[0].push(currentPayment);
              } else {
                paymentsTuple[1].push(currentPayment);
              }
              return paymentsTuple;
            },
            [[], []]
          );
          const sortedPayments = regularPayments.sort(
            (a: any, b: any) => moment(a.date) < moment(b.date)
          );
          sortedPayments.forEach((payment: any, i: number) => {
            payment.amount = toHuman(payment.amount);
            payment.onPress = () => {
              sheetRef?.current?.open();
              setSheetData(payment);
              setSelectedTableType(TableTypes.PAYMENTS);
              setSheetDataIndex(i);
            };
          });
          setPayments(sortedPayments);
          setScheduledPayments(scheduledPayments);
        })
        .then(async () => {
          if (!canViewAccountHistory || isAimPastResident) return;
          try {
            const residentInfo = await domusoApiService.getResident(
              currentProperty?.id,
              accountingId,
              yardiId
            );
            let runningBalance = 0;
            const transactions = residentInfo.transactions || residentInfo.ledger;
            if (Array.isArray(transactions) && transactions.length) {
              const sortField = transactions[0].TransDate ? "TransDate" : "date";
              const formattedTxs = transactions
                .sort((a: any, b: any) => moment(a[sortField]) > moment(b[sortField]))
                .map((t: any) => {
                  const txType = upperCase(t.TransType || t.category);
                  const isNotCharge = txType !== PaymentTypes.CHARGE;
                  let addAmount = t.ChargeAmount
                    ? Number(t.ChargeAmount) * 100
                    : Number(t.activity);
                  if (isNotCharge) addAmount *= -1;
                  runningBalance += addAmount;
                  return {
                    ...t,
                    type: t.TransType || t.category,
                    date: t.TransDate || t.date,
                    orderId: t.TransID || t.id,
                    amount: Number(toHuman(addAmount)),
                    balance: runningBalance,
                    // description: t.Description || t.description,
                  };
                })
                .sort((a: any, b: any) => moment(a[sortField]) < moment(b[sortField]));
              formattedTxs.forEach((t: any, i: number) => {
                t.onPress = () => {
                  sheetRef?.current?.open();
                  setSheetData(t);
                  setSelectedTableType(TableTypes.TRANSACTIONS);
                  setSheetDataIndex(i);
                };
              });
              setTransactions(formattedTxs);
            }
          } catch (error) {
            // console.log({ error });
            setTransactionsError(error);
          }
        })
        .catch(setPaymentsError)
        .finally(() => setLoading(false));
    } else {
      setNetworkError(true);
      return false;
    }
    setIsSwitchOn(allowedPayments === AllowedPaymentMethods.ANY && !!activeAutoPay?.schedule_date);
  }, [activeAutoPay, reservationId, allowedPayments, netInfo]);

  useFocusEffect(layoutEffect);

  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener((state) => {
      // console.log("Is connected?:", state.isConnected);
      let isVirtualDevice = isDeviceVirtual();
      if (isVirtualDevice) {
        setNetworkError(false);
        setNetInfo(true);
      } else {
        setNetworkError(!state.isConnected);
        setNetInfo(state.isConnected);
      }
    });
    return () => unsubscribe();
  }, []);

  const shiftDataSheetIndexHandler = (amount: number) => {
    const canDecrement = amount === -1 && sheetDataIndex > 0;
    const canIncrement = amount === 1 && sheetDataIndex < tableRowMaxIndex;
    if (canDecrement || canIncrement) {
      const newIndex = sheetDataIndex + amount;
      setSheetDataIndex(newIndex);
    }
  };

  const onToggleSwitch = () => {
    if (!hasEnabledPaymentMethods) {
      return setShowAllowPaymentsError(true);
    }

    if (isSwitchOn) {
      return setVisibleModal(true);
    }
    setIsSwitchOn(!isSwitchOn);
    return navigation.navigate("Actions", {
      screen: "PaymentAutoPayDetails",
      params: {
        ...params,
        accountingDetails,
        balanceDue,
        propertyUnit,
        reservationId,
        activeAutoPay,
        currentProperty,
        currentPropertyProfile,
        currentPropertyFeeProfile,
      },
    });
  };
  const cancelAutopay = () => {
    setAutopayCancelLoading(true);
    return domusoApiService
      .cancelAutoPay(reservationId)
      .then(async (resp) => {
        await logger.log({ resp, reservationId });
        return navigateToHomePage({ navigation, params });
      })
      .catch((e) => {
        logger.error(e);
        setApiError(true);
      })
      .finally(() => {
        setVisibleModal(false);
        setAutopayCancelLoading(false);
      });
  };

  return (
    <Screen style={styles.container} preset="scroll" unsafe={true} dontUseAutoPadding>
      <Grid>
        <Container>
          <Row>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow, styles.header]}>
              <View style={styles.inputRowInner}>
                <Stack space="small" style={styles.spaceBetweenColumn}>
                  <Stack space="smaller">
                    <Portal>
                      <AutoPayCancellationModal
                        visible={visibleModal}
                        setVisible={setVisibleModal}
                        isLoading={isAutopayCancelLoading}
                        propertyUnit={propertyUnit}
                        confirmCancellationCallback={cancelAutopay}
                      />
                    </Portal>
                    <View style={styles.propertyContainer}>
                      <Text
                        style={[styles.text, { fontWeight: "bold" }]}
                        {...automationTestingLocators.specificReservation.history.propName}>
                        {propName}
                      </Text>
                      <Text
                        style={styles.text}
                        {...automationTestingLocators.specificReservation.history.propAddress}>
                        {propAddress}
                      </Text>
                    </View>

                    {paymentDisabledMessage ? (
                      <>
                        <OnScreenAlert
                          alertType="alert"
                          title={paymentDisabledMessage}
                          titleStye={{ fontFamily: "Lato_700Bold" }}
                          {...automationTestingLocators.specificReservation.history
                            .paymentDisabledMessage}
                        />
                        <Spacer size="smaller" />
                      </>
                    ) : null}
                    {showAllowPaymentsError && !hasEnabledPaymentMethods && (
                      <OnScreenAlert
                        alertType="alert"
                        title={translate("homeStack.paymentsNotAllowedTitle") as string}
                        titleStye={{ fontFamily: "Lato_700Bold" }}
                        body={translate("homeStack.paymentsNotAllowedText") as string}
                        {...automationTestingLocators.specificReservation.history
                          .paymentsNotAllowedText}
                      />
                    )}

                    {apiError ? (
                      <OnScreenAlert
                        alertType="alert"
                        title={translate("homeStack.error") as string}
                        titleStye={{ fontFamily: "Lato_700Bold" }}
                        body={translate("homeStack.unexpectedError") as string}
                        {...automationTestingLocators.specificReservation.history.unexpectedError}
                      />
                    ) : null}
                  </Stack>
                  {/* TODO: add unit unassigned message */}
                  <ScheduledPaymentMessages
                    scheduledPayments={scheduledPayments}
                    propertyUnit={propertyUnit}
                    theme={theme}
                  />
                </Stack>
              </View>
            </Column>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
          </Row>
          <Row>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            <Column
              span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
              style={[styles.inputRow, { paddingHorizontal: 0 }, styles.tablePadding]}>
              <View>
                <Stack space="smaller">
                  <Headline
                    style={[styles.historyTitle, styles.horizontalPadding]}
                    {...automationTestingLocators.specificReservation.history.headline}>
                    {translate("homeStack.history")}
                  </Headline>
                  <View>
                    {canViewAccountHistory && (
                      <TableSelector
                        isPaymentsSelected={isPaymentsSelected}
                        setIsPaymentsSelected={setIsPaymentsSelected}
                        theme={theme}
                      />
                    )}
                    {isPaymentsSelected ? (
                      <PaymentTable
                        isLoading={isLoading}
                        payments={payments}
                        paymentsError={paymentsError}
                        networkError={networkError}
                      />
                    ) : (
                      <TransactionsTable
                        isLoading={isLoading}
                        transactions={transactions}
                        transactionsError={transactionsError}
                        aimResidentPastError={isAimPastResident}
                      />
                    )}
                  </View>
                  <View style={{ marginBottom: 10 }} />
                </Stack>
              </View>
            </Column>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
          </Row>
        </Container>
      </Grid>
      <BottomNav backgroundColor={theme.colors.white}></BottomNav>
      <Footer />
      <HistoryBottomSheet
        sheetRef={sheetRef}
        data={sheetDataValues}
        showLeftNavButton={showLeftNavButton}
        showRightNavButton={showRightNavButton}
        leftClickHandler={() => shiftDataSheetIndexHandler(-1)}
        rightClickHandler={() => shiftDataSheetIndexHandler(1)}
      />
    </Screen>
  );
};

let getPaymentValidationMessage: ({
  isIntegrated,
  isBlocked,
  isBlacklisted,
  allowedPayments,
  hasEnabledPaymentMethods,
  balanceDue,
}: {
  isIntegrated: boolean;
  isBlocked: boolean;
  isBlacklisted: boolean;
  allowedPayments: AllowedPaymentMethods;
  hasEnabledPaymentMethods: boolean;
  balanceDue: number | string;
}) => [string, boolean | undefined] = ({
  isIntegrated,
  isBlocked,
  isBlacklisted,
  allowedPayments,
  hasEnabledPaymentMethods,
  balanceDue,
}) => {
  let blacklistError = translate("homeStack.paymentsDisabledBlacklistMessage") || "";
  let sysDownError = translate("homeStack.paymentsDisabledSysOutageMessage") || "";
  let noOptionsError = translate("homeStack.notAcceptingPaymentsMessage") || "";

  // moves error conditions up the check-list as their presence influences other operations
  if (isBlacklisted) return [blacklistError, false];
  if (isIntegrated && !allowedPayments) return [sysDownError, true];

  const hasBalanceError = isBalanceError(balanceDue);
  if (hasBalanceError) return ["", true];
  if (!isBlocked) return ["", true];
  if (!hasEnabledPaymentMethods || isIntegrated) return [noOptionsError, false];
  return [noOptionsError, false];
};

const getStyles = (theme: ThemeType) => {
  const { colors, spacing } = theme;
  return StyleSheet.create({
    container: {
      backgroundColor: colors.background,
      paddingTop: spacing.large,
      zIndex: -1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: theme.colors.white,
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: theme.colors.white,
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: theme.colors.white,
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
    },
    text: {},
    historyTitle: {
      fontWeight: "bold",
      color: colors.darkBg,
    },
    horizontalPadding: {
      paddingHorizontal: theme.spacing.medium,
    },
    spaceBetweenColumn: {
      // paddingHorizontal: theme.spacing.medium,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    autoPayContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    autoPayTitle: {
      fontSize: theme.fontSize.large,
    },
    spaceBetweenRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    scrollView: { flexGrow: 0 },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      }),
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
      }),
    },
    inputRowInner: {
      justifyContent: "center",
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        webDesktop: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      }),
    },
    header: {
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingVertical: 3 },
        webTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        webDesktop: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        iosPhone: { paddingVertical: 3 },
        iosTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        androidPhone: { paddingVertical: 3 },
        androidTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
      }),
    },
    propertyContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: spacing.medium,
      paddingVertical: spacing.large,
      marginBottom: spacing.smaller,
      borderWidth: 1,
      borderColor: colors.white,
      backgroundColor: colors.white,
      zIndex: 2,
      elevation: 3,
      shadowColor: theme.colors.darkBg,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.24,
      shadowRadius: 3,
    },
    tablePadding: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingHorizontal: 0,
    },
    removeBlueOutline: {
      ...Platform.select({
        web: {
          outlineStyle: "none",
        } as any,
      }),
    },
  });
};

interface MonthlyAutoPayProps extends Record<string, any> {
  allowViaOverride: boolean | undefined;
  styles: any;
  isSwitchOn: any;
  isNewResident: any;
  allowedPayments: AllowedPaymentMethods;
  theme: ThemeType;
  nextPaymentDate?: any;
  onToggleSwitch: any;
}
const MonthlyAutoPay = ({
  allowViaOverride,
  styles,
  isSwitchOn,
  theme,
  isNewResident,
  hasEnabledPaymentMethods,
  onToggleSwitch,
  allowedPayments,
  nextPaymentDate = new Date(),
}: MonthlyAutoPayProps) => {
  const formattedDate = moment(nextPaymentDate).format("MMMM DD, yyyy");
  const onText = `Your account will auto-debited for the balance due on ${formattedDate}`;
  const disabled =
    !hasEnabledPaymentMethods ||
    isNewResident ||
    allowedPayments === AllowedPaymentMethods.CASH_EQUIVALENT ||
    (AllowedPaymentMethods.ANY && !allowViaOverride);
  const { spacing } = theme;
  return (
    <Stack>
      <View style={styles.autoPayContainer} accessible={true}>
        <Stack space="none" style={{ maxWidth: "85%" }}>
          <Headline style={styles.autoPayTitle}>{translate("homeStack.monthlyAutoPay")}</Headline>
          {isSwitchOn ? <Text style={{ marginTop: spacing.smaller }}>{onText}</Text> : null}
        </Stack>
        <Switch
          color={theme.colors.darkBg}
          value={isSwitchOn}
          onValueChange={onToggleSwitch}
          disabled={disabled}
          {...testProps("monthly-autopay")}
        />
      </View>
      {disabled ? (
        <ErrorMessage
          formName="dna"
          errors={{
            dna: { message: translate("homeStack.autoPayNotAvailable") },
          }}
        />
      ) : null}
    </Stack>
  );
};

interface ScheduledPayment {
  amount: number;
  date: Date;
  date_only: Date;
  orderId: string;
  type: string;
  status: string;
}
interface ScheduledPaymentMessagesProps {
  scheduledPayments: ScheduledPayment[];
  propertyUnit: string | number;
  theme: ThemeType;
}
const ScheduledPaymentMessages = ({
  scheduledPayments,
  propertyUnit,
  theme: { spacing },
}: ScheduledPaymentMessagesProps) => {
  if (!scheduledPayments || !Array.isArray(scheduledPayments)) {
    return null;
  }
  return (
    <Stack space="none">
      {scheduledPayments.map((payment) => {
        const amount = toHuman(payment.amount);
        const { date_only } = payment;
        //Since date_only doesn't have time zone information moment will not try to adjust the time zone which is what we need since DB stores time in PST.
        //This date object will result in e.g 11/16/2023 00:00:00 PST and if we where in other time zone it will still be e.g 11/16/2023 00:00:00 CT
        const momentDate = moment(date_only, "MM/DD/YYYY");
        const formattedDate = momentDate.format("MMMM DD, YYYY");
        const momentNow = moment();
        const difference = momentDate.clone().diff(momentNow.startOf("day"), "days");
        if (difference === 0) {
          return null;
        }
        let diffString = difference === 1 ? `1 more day` : `${difference} more days`;
        const unitNumberStr = propertyUnit ? `for ${deriveUnitTitle(propertyUnit)} ` : ``;
        return (
          <InfoBox
            key={nanoid()}
            width={"100%"}
            usesXClose
            style={{ marginBottom: spacing.small }}
            headline={translate("homeStack.upcomingScheduledPayment")}
            {...automationTestingLocators.specificReservation.history.upcomingScheduledPayment}
            infoBoxType={InfoBoxType.BLUE}>
            <>
              <Text
                {...automationTestingLocators.specificReservation.history
                  .upcomingScheduledPaymentBody}>
                {translate("homeStack.upcomingScheduledPaymentBody", {
                  amount,
                  unitNumberStr,
                  formattedDate,
                  diffString,
                })}
              </Text>
            </>
          </InfoBox>
        );
      })}
    </Stack>
  );
};

interface AutoPayCancellationModalProps {
  visible: boolean;
  setVisible: any;
  isLoading: boolean;
  confirmCancellationCallback: any;
  propertyUnit?: string;
}
const AutoPayCancellationModal = ({
  visible,
  setVisible,
  isLoading,
  propertyUnit,
  confirmCancellationCallback,
}: AutoPayCancellationModalProps) => {
  const theme = useTheme() as ThemeType;
  const { colors, spacing, fontSize } = theme;
  const unitNumberStr = propertyUnit ? ` for ${deriveUnitTitle(propertyUnit)}` : "";
  const styles = StyleSheet.create({
    modalContainer: {
      display: "flex",
      marginHorizontal: spacing.medium,
      backgroundColor: colors.background,
      borderRadius: 10,
      flexDirection: "column",
    },
    outerContainer: {
      display: "flex",
      padding: spacing.medium,
      flexDirection: "column",
      justifyContent: "space-between",
    },
    buttonContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-around",
    },
    button: {
      width: 136,
    },
    textStyle: {
      fontSize: fontSize.large,
    },
    headlineStyle: {
      fontSize: fontSize.large,
      fontWeight: "600",
    },
    noButton: {
      backgroundColor: colors.white,
      borderWidth: 1,
      borderColor: colors.darkBg,
    },
  });
  const dismissModal = () => setVisible(false);
  return (
    <Modal visible={visible} onDismiss={dismissModal} contentContainerStyle={styles.modalContainer}>
      <View style={styles.outerContainer}>
        <Stack>
          <Headline
            style={styles.headlineStyle}
            {...automationTestingLocators.specificReservation.cancelAutopayHeader}>
            {translate("homeStack.cancelAutopay")}
          </Headline>
          <Text
            style={styles.textStyle}
            {...automationTestingLocators.specificReservation.cancelAutopayText}>
            {translate("homeStack.autopayCancellationText", { unitNumberStr })}
          </Text>
        </Stack>
        <Spacer size="massive"></Spacer>
        <View style={styles.buttonContainer} accessible={true}>
          <Button
            style={{ ...styles.button, ...styles.noButton }}
            mode="contained"
            tx="componentStatic.no"
            disabled={isLoading}
            onPress={dismissModal}
            {...automationTestingLocators.specificReservation.autoPayNo}
          />
          <Button
            style={styles.button}
            mode="contained"
            tx="componentStatic.yes"
            disabled={isLoading}
            loading={isLoading}
            onPress={confirmCancellationCallback}
            {...automationTestingLocators.specificReservation.autoPayYes}
          />
        </View>
      </View>
    </Modal>
  );
};

interface TableSelectorProps {
  isPaymentsSelected: boolean;
  setIsPaymentsSelected: Function;
  theme: ThemeType;
}

const TableSelector = ({
  isPaymentsSelected,
  setIsPaymentsSelected,
  theme,
}: TableSelectorProps) => {
  const { colors, spacing } = theme;
  const toggleView = () => {
    if (isPaymentsSelected) {
      setIsPaymentsSelected(false);
    } else {
      setIsPaymentsSelected(true);
    }
  };
  const styles = StyleSheet.create({
    container: {
      width: "100%",
      paddingHorizontal: spacing.small,
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.medium,
    },
    column: { width: "49%", marginBottom: spacing.tiny, ...getStyles(theme).removeBlueOutline },
    tableName: { textAlign: "center", marginBottom: spacing.small },
    line: { height: 2, backgroundColor: colors.grey },
    selectedLine: { height: 3, backgroundColor: colors.darkBg },
    text: { color: colors.darkGrey },
    selectedText: { color: colors.text },
  });
  const paymentsNameClass = isPaymentsSelected ? styles.selectedText : styles.text;
  const paymentsLineClass = isPaymentsSelected ? styles.selectedLine : styles.line;
  const accountsNameClass = isPaymentsSelected ? styles.text : styles.selectedText;
  const accountLineClass = isPaymentsSelected ? styles.line : styles.selectedLine;
  return (
    <View style={styles.container}>
      <Pressable
        onPress={toggleView}
        style={styles.column}
        {...automationTestingLocators.specificReservation.history.togglePaymentHeader}>
        <View>
          <Text
            style={[styles.tableName, paymentsNameClass]}
            tx="homeStack.payment"
            {...automationTestingLocators.specificReservation.history.paymentHeader}
          />
          <View style={paymentsLineClass} />
        </View>
      </Pressable>
      <Pressable
        onPress={toggleView}
        style={styles.column}
        {...automationTestingLocators.specificReservation.history.toggleAccountHeader}>
        <View>
          <Text
            style={[styles.tableName, accountsNameClass]}
            tx="homeStack.account"
            {...automationTestingLocators.specificReservation.history.accountHeader}
          />
          <View style={accountLineClass} />
        </View>
      </Pressable>
    </View>
  );
};
